// Interactive flowchart designer — drop-in editor used by the Add /
// Edit screens for Process, SubProcess, and Service. Web-only; the
// caller is expected to gate on Platform.OS === 'web' or fall back
// to <FlowchartViewer /> when on native.
//
// Responsibilities:
//   1. Hydrate React Flow state from the parent's `value` (or
//      EMPTY_FLOWCHART).
//   2. Let users add new nodes (4 shapes), drag them, connect them,
//      relabel them by double-clicking, and delete them.
//   3. Fire `onChange` with a normalised FlowchartData payload every
//      time the diagram structurally changes — caller decides when to
//      persist (typically on form submit).
//
// Design notes:
//   * `value` is the source of truth. We do NOT keep an independent
//     internal copy beyond what React Flow needs for its hooks.
//   * Adding a node uses crypto.randomUUID() for the id — keeps the
//     ids stable across reorders and avoids index-based collisions.
//   * Labels are edited via a tiny inline modal because React Flow's
//     built-in node `data` doesn't expose a natural way to edit
//     in-place without a third-party library.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ReactFlow,
  ReactFlowProvider,
  useReactFlow,
  Background,
  Controls,
  MiniMap,
  addEdge,
  applyEdgeChanges,
  applyNodeChanges,
  type Edge,
  type EdgeChange,
  type Node,
  type NodeChange,
  type Connection,
  MarkerType,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { FLOWCHART_NODE_TYPES_MAP } from './nodes';
import {
  EMPTY_FLOWCHART,
  FLOWCHART_NODE_TYPES,
  type FlowchartData,
  type FlowchartNodeType,
} from './types';

interface Props {
  value: FlowchartData | null | undefined;
  onChange: (next: FlowchartData) => void;
  height?: number;          // pixels; defaults to 480
  // When provided, the toolbar renders an "🪄 توليد تلقائي" button.
  // The callback is expected to return the freshly built diagram
  // (typically via buildLinearFlowchart from the parent's step state)
  // and the designer will replace the canvas with it. Returning null
  // is a no-op (e.g. parent decided there are no steps yet).
  onAutoGenerate?: () => FlowchartData | null;
  toolbarLabels?: {
    start?: string;
    step?: string;
    decision?: string;
    end?: string;
    addNode?: string;
    deleteSelected?: string;
    editLabel?: string;
    saveLabel?: string;
    cancel?: string;
    placeholderLabel?: string;
    instruction?: string;
    autoGenerate?: string;
    autoGenerateConfirmTitle?: string;
    autoGenerateConfirmBody?: string;
    autoGenerateEmptySteps?: string;
    autoGenerateProceed?: string;
  };
}

// Generate a stable-ish id without a uuid dep. Math.random + timestamp
// keeps collisions astronomically unlikely for the small diagrams the
// directorate is editing in a session.
function makeId(): string {
  return `n_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

// Convert the FlowchartData wire shape → React Flow's Node[] / Edge[].
function toReactFlow(value: FlowchartData | null | undefined): {
  nodes: Node[];
  edges: Edge[];
} {
  const v = value || EMPTY_FLOWCHART;
  const nodes: Node[] = v.nodes.map((n) => ({
    id: n.id,
    type: n.type,
    position: n.position || { x: 0, y: 0 },
    data: { label: n.label || '' },
  }));
  const edges: Edge[] = v.edges.map((e) => ({
    id: e.id,
    source: e.source,
    target: e.target,
    sourceHandle: e.sourceHandle,
    targetHandle: e.targetHandle,
    label: e.label,
    markerEnd: { type: MarkerType.ArrowClosed },
  }));
  return { nodes, edges };
}

// Pack React Flow's Node[] / Edge[] back into the wire shape.
function fromReactFlow(nodes: Node[], edges: Edge[]): FlowchartData {
  // Drop any edge whose source/target node no longer exists. React Flow's
  // native node removal (Delete key / programmatic deleteElements) emits the
  // node 'remove' change before — and in a separate commit from — the
  // connected edge removals, so a serialize can momentarily see a node gone
  // while its edges linger. Only the explicit "delete selected" button prunes
  // edges eagerly; this guard makes every path safe so we never emit (and the
  // user never saves) a dangling edge that the backend validator rejects with
  // "الوصلة … تشير إلى عقدة غير موجودة".
  const nodeIds = new Set(nodes.map((n) => n.id));
  return {
    version: '1.0',
    nodes: nodes.map((n) => ({
      id: n.id,
      type: (n.type || 'step') as FlowchartNodeType,
      label: ((n.data as any)?.label as string) || '',
      position: n.position,
    })),
    edges: edges
      .filter((e) => nodeIds.has(e.source) && nodeIds.has(e.target))
      .map((e) => ({
        id: e.id,
        source: e.source,
        target: e.target,
        label: typeof e.label === 'string' ? e.label : undefined,
        sourceHandle: typeof e.sourceHandle === 'string' ? e.sourceHandle : undefined,
        targetHandle: typeof e.targetHandle === 'string' ? e.targetHandle : undefined,
      })),
  };
}

function FlowchartDesignerInner({ value, onChange, height = 480, toolbarLabels, onAutoGenerate }: Props) {
  const initial = useMemo(() => toReactFlow(value), [/* hydrate once */]);  // eslint-disable-line react-hooks/exhaustive-deps
  const [nodes, setNodes] = useState<Node[]>(initial.nodes);
  const [edges, setEdges] = useState<Edge[]>(initial.edges);

  // Re-hydrate when the parent swaps the `value` reference (e.g. on
  // edit-mode load after the API resolves). Comparing JSON keeps us
  // from looping when our own onChange round-trips the same shape.
  useEffect(() => {
    const incoming = toReactFlow(value);
    const sameNodes = JSON.stringify(nodes) === JSON.stringify(incoming.nodes);
    const sameEdges = JSON.stringify(edges) === JSON.stringify(incoming.edges);
    if (!sameNodes || !sameEdges) {
      setNodes(incoming.nodes);
      setEdges(incoming.edges);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const labels = {
    start:    toolbarLabels?.start    ?? 'بداية',
    step:     toolbarLabels?.step     ?? 'خطوة',
    decision: toolbarLabels?.decision ?? 'قرار',
    end:      toolbarLabels?.end      ?? 'نهاية',
    addNode:  toolbarLabels?.addNode  ?? 'إضافة',
    deleteSelected:  toolbarLabels?.deleteSelected  ?? 'حذف المحدد',
    editLabel:       toolbarLabels?.editLabel       ?? 'تعديل النص',
    saveLabel:       toolbarLabels?.saveLabel       ?? 'حفظ',
    cancel:          toolbarLabels?.cancel          ?? 'إلغاء',
    placeholderLabel: toolbarLabels?.placeholderLabel ?? 'عنوان العقدة',
    instruction:     toolbarLabels?.instruction     ?? 'انقر مزدوجاً على أي عقدة لتعديل نصها. اسحب من القاعدة لربط العقد.',
    autoGenerate:    toolbarLabels?.autoGenerate    ?? '🪄 توليد تلقائي من الخطوات',
    autoGenerateConfirmTitle: toolbarLabels?.autoGenerateConfirmTitle ?? 'استبدال الرسم الحالي؟',
    autoGenerateConfirmBody:  toolbarLabels?.autoGenerateConfirmBody  ?? 'سيتم استبدال الرسم الموجود برسم جديد مبني من خطوات العملية. لا يمكن التراجع عن هذه العملية.',
    autoGenerateEmptySteps:   toolbarLabels?.autoGenerateEmptySteps   ?? 'أضف خطوات العملية أولاً ثم اضغط على زر التوليد التلقائي.',
    autoGenerateProceed:      toolbarLabels?.autoGenerateProceed      ?? 'توليد',
  };

  // React-idiomatic side-effect plumbing for onChange:
  //
  // Calling `onChange(parent setState)` directly inside a setNodes
  // updater function is an anti-pattern — React may invoke the
  // updater during reconciliation, which then triggers the parent's
  // setState during render and logs:
  //   "Cannot update a component while rendering a different component"
  //
  // Fix: setNodes/setEdges updaters stay PURE. Handlers set a flag on
  // pendingEmitRef.current when the change is one the parent cares
  // about (add / connect / drag-end / remove — but NOT mid-drag
  // position ticks, which would thrash the form state). A useEffect
  // observes nodes/edges and emits onChange after commit when the
  // flag is set.
  const pendingEmitRef = useRef(false);
  const isFirstEmitRef = useRef(true);

  useEffect(() => {
    if (isFirstEmitRef.current) {
      // Don't echo the hydrated state back as a "change" on mount.
      isFirstEmitRef.current = false;
      return;
    }
    if (pendingEmitRef.current) {
      pendingEmitRef.current = false;
      onChange(fromReactFlow(nodes, edges));
    }
  }, [nodes, edges, onChange]);

  const handleNodesChange = useCallback((changes: NodeChange[]) => {
    // Only flag the parent when drag-end OR remove fires. Mid-drag
    // ticks come through with dragging=true; selection-only changes
    // come through as type='select' — both must NOT trigger emit.
    if (changes.some((c) =>
      c.type === 'remove'
      || (c.type === 'position' && c.dragging === false)
    )) {
      pendingEmitRef.current = true;
    }
    setNodes((prev) => applyNodeChanges(changes, prev));
  }, []);

  const handleEdgesChange = useCallback((changes: EdgeChange[]) => {
    if (changes.some((c) => c.type === 'remove')) {
      pendingEmitRef.current = true;
    }
    setEdges((prev) => applyEdgeChanges(changes, prev));
  }, []);

  const handleConnect = useCallback((connection: Connection) => {
    pendingEmitRef.current = true;
    setEdges((prev) => addEdge({
      ...connection,
      id: `e_${makeId()}`,
      markerEnd: { type: MarkerType.ArrowClosed },
    }, prev));
  }, []);

  // Stagger newly-added nodes a bit so they don't pile on top of each
  // other when the user spams the "add" buttons.
  const [addCount, setAddCount] = useState(0);

  const addNode = useCallback((type: FlowchartNodeType) => {
    const offsetCount = addCount;
    setAddCount((c) => c + 1);
    const newNode: Node = {
      id: makeId(),
      type,
      position: { x: 80 + offsetCount * 30, y: 80 + offsetCount * 30 },
      data: { label: labels[type] },
    };
    pendingEmitRef.current = true;
    setNodes((prev) => [...prev, newNode]);
  }, [addCount, labels]);

  const deleteSelected = useCallback(() => {
    const selectedNodeIds = new Set(nodes.filter((n) => n.selected).map((n) => n.id));
    const selectedEdgeIds = new Set(edges.filter((e) => e.selected).map((e) => e.id));
    if (selectedNodeIds.size === 0 && selectedEdgeIds.size === 0) return;

    // When a node is removed, drop any edge attached to it too —
    // otherwise we'd leave dangling edges and the backend validator
    // would reject the payload.
    const nextNodes = nodes.filter((n) => !selectedNodeIds.has(n.id));
    const nextEdges = edges.filter((e) =>
      !selectedEdgeIds.has(e.id)
      && !selectedNodeIds.has(e.source)
      && !selectedNodeIds.has(e.target)
    );
    pendingEmitRef.current = true;
    setNodes(nextNodes);
    setEdges(nextEdges);
  }, [nodes, edges]);

  // Auto-generate-from-steps confirmation state.
  //
  // The button is split from "add a node" because it WIPES the canvas:
  //   * No existing nodes → directly run onAutoGenerate and apply.
  //   * Existing nodes    → open the confirm modal first so the user
  //                         doesn't lose manual edits accidentally.
  //   * Parent returns null (no steps yet) → show the empty-steps
  //                         informational modal so the user gets a
  //                         clear hint instead of a silent no-op.
  const [autoGenModal, setAutoGenModal] = useState<'confirm' | 'empty' | null>(null);

  // Re-frame the viewport after auto-generate. The <ReactFlow fitView>
  // prop only fits ONCE on mount (when the canvas is usually empty), so a
  // freshly generated linear diagram — laid out across a wide x-range —
  // lands mostly outside the current viewport and looks like it "didn't
  // appear / disappeared". This flag asks the effect below to fitView once
  // the new nodes have been measured by React Flow.
  const fitPendingRef = useRef(false);
  const { fitView } = useReactFlow();

  useEffect(() => {
    if (fitPendingRef.current && nodes.length > 0) {
      fitPendingRef.current = false;
      // Defer a frame so React Flow has measured the new nodes; fitView
      // is a no-op until node dimensions are known.
      const raf = requestAnimationFrame(() => fitView({ padding: 0.2, duration: 300 }));
      return () => cancelAnimationFrame(raf);
    }
  }, [nodes, fitView]);

  const applyAutoGenerate = useCallback(() => {
    if (!onAutoGenerate) return;
    const generated = onAutoGenerate();
    if (!generated || generated.nodes.length === 0) {
      setAutoGenModal('empty');
      return;
    }
    // Hydrate React Flow state directly so the diagram appears
    // immediately, then mark a pending emit so the parent receives
    // the same payload via onChange after commit.
    const rf = toReactFlow(generated);
    pendingEmitRef.current = true;
    fitPendingRef.current = true;  // frame the new diagram once measured
    setNodes(rf.nodes);
    setEdges(rf.edges);
    setAutoGenModal(null);
  }, [onAutoGenerate]);

  const handleAutoGenerateClick = useCallback(() => {
    if (!onAutoGenerate) return;
    if (nodes.length === 0 && edges.length === 0) {
      // Empty canvas — skip confirmation, just generate.
      applyAutoGenerate();
      return;
    }
    setAutoGenModal('confirm');
  }, [onAutoGenerate, nodes.length, edges.length, applyAutoGenerate]);

  // Inline label-editing modal — fired on double-click of a node.
  const [editingNodeId, setEditingNodeId] = useState<string | null>(null);
  const [draftLabel, setDraftLabel] = useState('');

  const handleNodeDoubleClick = useCallback((_e: React.MouseEvent, node: Node) => {
    setEditingNodeId(node.id);
    setDraftLabel(((node.data as any)?.label as string) || '');
  }, []);

  const commitLabel = useCallback(() => {
    if (!editingNodeId) return;
    const nextNodes = nodes.map((n) =>
      n.id === editingNodeId ? { ...n, data: { ...n.data, label: draftLabel } } : n
    );
    pendingEmitRef.current = true;
    setNodes(nextNodes);
    setEditingNodeId(null);
  }, [editingNodeId, draftLabel, nodes]);

  const cancelLabel = useCallback(() => {
    setEditingNodeId(null);
  }, []);

  return (
    <div style={{ position: 'relative', direction: 'rtl' }}>
      {/* Toolbar */}
      <div style={toolbarStyle}>
        <span style={instructionStyle}>{labels.instruction}</span>
        <div style={buttonGroupStyle}>
          {onAutoGenerate && (
            <button
              type="button"
              onClick={handleAutoGenerateClick}
              style={autoGenerateButtonStyle}
              title={labels.autoGenerate}
            >
              {labels.autoGenerate}
            </button>
          )}
          {FLOWCHART_NODE_TYPES.map((type) => (
            <button
              key={type}
              type="button"
              onClick={() => addNode(type)}
              style={addButtonStyle(type)}
              title={labels.addNode}
            >
              + {labels[type]}
            </button>
          ))}
          <button
            type="button"
            onClick={deleteSelected}
            style={deleteButtonStyle}
            title={labels.deleteSelected}
          >
            ✕ {labels.deleteSelected}
          </button>
        </div>
      </div>

      {/* Canvas */}
      <div style={{
        height, border: '1px solid #E2E8F0', borderRadius: 8,
        backgroundColor: '#FAFAFA', overflow: 'hidden',
      }}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={handleNodesChange}
          onEdgesChange={handleEdgesChange}
          onConnect={handleConnect}
          onNodeDoubleClick={handleNodeDoubleClick}
          nodeTypes={FLOWCHART_NODE_TYPES_MAP}
          fitView
          // Default to a clean, dotted grid background.
          defaultEdgeOptions={{ markerEnd: { type: MarkerType.ArrowClosed } }}
        >
          <Background gap={20} size={1} color="#CBD5E1" />
          <Controls position="bottom-left" showInteractive={false} />
          <MiniMap pannable zoomable position="bottom-right" />
        </ReactFlow>
      </div>

      {/* Auto-generate confirmation / empty-steps notice */}
      {autoGenModal === 'confirm' && (
        <div style={modalOverlayStyle} onClick={() => setAutoGenModal(null)}>
          <div style={modalCardStyle} onClick={(e) => e.stopPropagation()}>
            <div style={modalTitleStyle}>{labels.autoGenerateConfirmTitle}</div>
            <div style={{ fontSize: 13, color: '#475569', marginBottom: 16, textAlign: 'right', lineHeight: 1.6 }}>
              {labels.autoGenerateConfirmBody}
            </div>
            <div style={modalActionsStyle}>
              <button type="button" onClick={() => setAutoGenModal(null)} style={modalCancelButtonStyle}>
                {labels.cancel}
              </button>
              <button type="button" onClick={applyAutoGenerate} style={modalSaveButtonStyle}>
                {labels.autoGenerateProceed}
              </button>
            </div>
          </div>
        </div>
      )}
      {autoGenModal === 'empty' && (
        <div style={modalOverlayStyle} onClick={() => setAutoGenModal(null)}>
          <div style={modalCardStyle} onClick={(e) => e.stopPropagation()}>
            <div style={modalTitleStyle}>{labels.autoGenerate}</div>
            <div style={{ fontSize: 13, color: '#475569', marginBottom: 16, textAlign: 'right', lineHeight: 1.6 }}>
              {labels.autoGenerateEmptySteps}
            </div>
            <div style={modalActionsStyle}>
              <button type="button" onClick={() => setAutoGenModal(null)} style={modalSaveButtonStyle}>
                {labels.cancel /* user dismisses the info notice */}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Inline label editor */}
      {editingNodeId && (
        <div style={modalOverlayStyle} onClick={cancelLabel}>
          <div style={modalCardStyle} onClick={(e) => e.stopPropagation()}>
            <div style={modalTitleStyle}>{labels.editLabel}</div>
            <input
              autoFocus
              value={draftLabel}
              onChange={(e) => setDraftLabel(e.target.value)}
              placeholder={labels.placeholderLabel}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitLabel();
                if (e.key === 'Escape') cancelLabel();
              }}
              style={modalInputStyle}
            />
            <div style={modalActionsStyle}>
              <button type="button" onClick={cancelLabel} style={modalCancelButtonStyle}>
                {labels.cancel}
              </button>
              <button type="button" onClick={commitLabel} style={modalSaveButtonStyle}>
                {labels.saveLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// React Flow requires a Provider in scope for its internal state.
// Wrapping at the public export lets callers drop the designer in
// without remembering the provider.
export default function FlowchartDesigner(props: Props) {
  return (
    <ReactFlowProvider>
      <FlowchartDesignerInner {...props} />
    </ReactFlowProvider>
  );
}

// ────────────────────────────────────────────────────────────────────
// Style objects — kept inline (web only) instead of StyleSheet because
// React Native StyleSheet doesn't help here and React Flow CSS is the
// authoritative source for canvas-internal styling.
// ────────────────────────────────────────────────────────────────────

const toolbarStyle: React.CSSProperties = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  marginBottom: 8, flexWrap: 'wrap', gap: 8,
};

const instructionStyle: React.CSSProperties = {
  fontSize: 12, color: '#64748B',
};

const buttonGroupStyle: React.CSSProperties = {
  display: 'flex', flexDirection: 'row-reverse', gap: 6, flexWrap: 'wrap',
};

const addButtonStyle = (type: FlowchartNodeType): React.CSSProperties => {
  const palette: Record<FlowchartNodeType, { bg: string; fg: string; border: string }> = {
    start:    { bg: '#DCFCE7', fg: '#14532D', border: '#16A34A' },
    step:     { bg: '#DBEAFE', fg: '#1E3A8A', border: '#2563EB' },
    decision: { bg: '#FEF3C7', fg: '#78350F', border: '#D97706' },
    end:      { bg: '#FEE2E2', fg: '#7F1D1D', border: '#DC2626' },
  };
  const c = palette[type];
  return {
    padding: '6px 12px',
    borderRadius: 6,
    border: `1px solid ${c.border}`,
    backgroundColor: c.bg,
    color: c.fg,
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
  };
};

const deleteButtonStyle: React.CSSProperties = {
  padding: '6px 12px',
  borderRadius: 6,
  border: '1px solid #E2E8F0',
  backgroundColor: '#FFFFFF',
  color: '#64748B',
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
};

// Highlight the auto-generate button with the brand primary so it
// reads as the "magic" action, distinct from the per-shape add buttons.
const autoGenerateButtonStyle: React.CSSProperties = {
  padding: '6px 12px',
  borderRadius: 6,
  border: '1px solid #A9211F',
  backgroundColor: '#A9211F',
  color: '#FFFFFF',
  fontSize: 12,
  fontWeight: 700,
  cursor: 'pointer',
};

const modalOverlayStyle: React.CSSProperties = {
  position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 9999,
};

const modalCardStyle: React.CSSProperties = {
  backgroundColor: '#FFFFFF',
  padding: 24,
  borderRadius: 12,
  minWidth: 320,
  maxWidth: '90vw',
  boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
  direction: 'rtl',
};

const modalTitleStyle: React.CSSProperties = {
  fontSize: 16, fontWeight: 700, marginBottom: 12, textAlign: 'right',
};

const modalInputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid #E2E8F0',
  borderRadius: 8,
  fontSize: 14,
  textAlign: 'right',
  boxSizing: 'border-box',
};

const modalActionsStyle: React.CSSProperties = {
  display: 'flex', justifyContent: 'flex-start', gap: 8, marginTop: 16,
};

const modalSaveButtonStyle: React.CSSProperties = {
  padding: '10px 20px',
  borderRadius: 8,
  border: 'none',
  backgroundColor: '#A9211F',
  color: '#FFFFFF',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
};

const modalCancelButtonStyle: React.CSSProperties = {
  padding: '10px 20px',
  borderRadius: 8,
  border: '1px solid #E2E8F0',
  backgroundColor: '#FFFFFF',
  color: '#64748B',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
};
